"use client";

import { useQueries } from "@tanstack/react-query";
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { GLOBAL_CHAT_CONSTANTS } from "@/components/global-chat/chat-constants";
import { CallPatient } from "@/components/patient/call-modal";
import type { CallEndResult } from "@/components/patient/call-modal/manual-call";
import { useProgressNavigation } from "@/components/progress-bar";
import { queryClient } from "@/config/react-query";
import { useIsMobile } from "@/contexts/is-mobile-provider";
import { useTenant } from "@/contexts/tenant-provider";
import { useChatRealtimeCache } from "@/hooks/use-chat-realtime-cache";
import { useEmailRealtimeCache } from "@/hooks/use-email-realtime-cache";
import { useFaxRealtimeCache } from "@/hooks/use-fax-realtime-cache";
import { useLocations } from "@/hooks/use-locations";
import { useSocialRealtimeCache } from "@/hooks/use-social-realtime-cache";
import { parseError } from "@/lib/parse-error";
import { useToast } from "@/lib/use-toast";
import { CallDetail, ChatDetail, ChatFilters, ChatWindowState } from "@/types/chat";
import { MessageSummary } from "@/types/message";
import { useUser } from "./user-provider";
import { patientSidebarDefinition } from "@/features/sidebar/patient-sidebar";
import { chatQueryKeys, chatQueryOptions } from "@/query/comms/chat";
import { useChatFiltersStore, useChatStore } from "@/stores/chat-store";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";
export function isCallSummary(message: CallDetail | MessageSummary): message is CallDetail {
  return (message as CallDetail).completedDate !== undefined;
}
export function isMessageSummary(message: CallDetail | MessageSummary): message is MessageSummary {
  return (message as MessageSummary).content !== undefined;
}
export interface LastCallEnd {
  patientId: string;
  /** Someone (or a voicemail) answered; null when it can't be known. */
  pickedUp: boolean | null;
  /** Handed to the phone app, which places the call and sees it end. */
  handedOff?: boolean;
}
interface CommunicationContextType {
  // Call related
  activeCall: {
    patient: CallPatient;
  } | null;
  startCall: (patient: CallPatient) => void;
  endCall: (result?: CallEndResult) => void;
  /** The last call that ended here, and whether anyone picked up. */
  lastCallEnd: LastCallEnd | null;

  // Chat related
  activeChats: ChatWindowState[];
  openChat: (patientId?: string, chatId?: string, onOpen?: (chatId: string) => void) => Promise<void>;
  closeChat: (identifier: string | ChatWindowState) => void;
  minimizeChat: (identifier: string | ChatWindowState) => void;
  maximizeChat: (identifier: string | ChatWindowState) => void;
  visibleChats: ChatWindowState[];
  hiddenChats: ChatWindowState[];
  isAddChatOpen: boolean;
  setIsAddChatOpen: (isOpen: boolean) => void;

  // Chat list related (UI state only)
  selectedChatId: string | undefined;

  // Chat filters
  chatFilters: ChatFilters;
  setChatFilters: (filters: ChatFilters) => void;
}
const CommunicationContext = createContext<CommunicationContextType | undefined>(undefined);
export function CommunicationProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    toast
  } = useToast();
  const {
    tenant
  } = useTenant();
  const searchParams = useSearchParams();
  const {
    isMobile
  } = useIsMobile();
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const selectedChatId = searchParams.get("chatId") ?? undefined;

  // Use realtime cache hooks to handle websocket updates
  useChatRealtimeCache();
  useEmailRealtimeCache();
  useSocialRealtimeCache();
  useFaxRealtimeCache();
  const previousTenantRef = useRef<string | undefined>(undefined);
  const [activeCall, setActiveCall] = useState<{
    patient: CallPatient;
  } | null>(null);
  const [lastCallEnd, setLastCallEnd] = useState<LastCallEnd | null>(null);
  const [isAddChatOpen, setIsAddChatOpen] = useState(false);
  const {
    currentUser,
    currentLocationId
  } = useUser();
  const {
    locations,
    isLocationsLoading
  } = useLocations();

  // Use Zustand stores
  const {
    activeChats,
    setActiveChats,
    addActiveChat,
    removeActiveChat,
    updateActiveChat
  } = useChatStore();
  const closeSidebar = useRightSidebarStore(state => state.close);
  const {
    chatFilters,
    setChatFilters
  } = useChatFiltersStore();
  const chatDetails = useQueries({
    queries: activeChats.map(chat => ({
      ...chatQueryOptions.detail(chat.chatId ?? ""),
      enabled: !!chat.chatId
    }))
  });
  useEffect(() => {
    if (currentUser === undefined || isLocationsLoading) return;
    const currentFilters = useChatFiltersStore.getState().chatFilters;
    const persistedLocationId = currentFilters.locationId;

    // Only ever CLEAR a stale location, never set one. The filter is opt-in:
    // forcing it on hid every chat whose patient has no `primaryLocationId`,
    // which is most of them on clinics that migrated in or that mostly text.
    //
    // `canFilterByLocation` mirrors the render condition of the "My Location"
    // chip in `comms/chat/sidebar/header.tsx` and `global-chat/messaging-sidebar.tsx`
    // — BOTH of its clauses. A location that survived here while the chip is
    // hidden would be an active filter with no visible control — invisible and
    // unclearable — so drop it whenever the chip is not on screen to turn it
    // off. Keep this in step with those two render conditions.
    const canFilterByLocation = locations.length > 1 && tenant?.enabledModules?.includes("LOCATIONS") === true;
    const nextLocationId = canFilterByLocation && persistedLocationId && currentLocationId && locations.some(location => location.id === persistedLocationId) ? persistedLocationId : undefined;
    if (nextLocationId !== persistedLocationId) {
      setChatFilters({
        ...currentFilters,
        locationId: nextLocationId
      });
    }
  }, [chatFilters.locationId, currentUser, currentLocationId, isLocationsLoading, locations, tenant?.enabledModules, setChatFilters]);
  const startCall = (patient: CallPatient) => {
    setActiveCall({
      patient
    });
  };
  const endCall = (result?: CallEndResult) => {
    if (activeCall) {
      setLastCallEnd({
        patientId: activeCall.patient.id,
        pickedUp: result?.pickedUp ?? null,
        handedOff: result?.handedOff
      });
    }
    setActiveCall(null);
  };
  const maximizeChat = (identifier: string | ChatWindowState) => {
    updateActiveChat(identifier, chat_0 => ({
      ...chat_0,
      isMinimized: false
    }));
  };

  // Move a chat to the front of the array (the rightmost visible window) and
  // un-minimize it. Unconditional: checking whether it is already visible
  // needs `visibleCount`, which comes from the render closure and is stale by
  // the time an awaited request resolves. Reads the live store for the same
  // reason — writing back a stale list would resurrect resolved placeholders.
  const bringChatToFront = (chatId: string) => {
    const currentChats = useChatStore.getState().activeChats;
    const chat_1: ChatWindowState = {
      chatId,
      isMinimized: false
    };
    setActiveChats([chat_1, ...currentChats.filter(c => c.chatId !== chatId)]);
  };

  /**
   * Chat ordering in activeChats array:
   * [newest, new, ...oldest]
   * First 2 items are the visible chats, shown left-to-right
   * (1 when the add-chat window is open). All other chats are hidden in the dropdown.
   *
   * Example: activeChats = [newest, new, old, oldest]
   * Visible: [newest, new] -> displays as: [newest] [new]
   * Hidden: [old, oldest]
   */

  // Auto-evict chats whose detail query has errored (e.g., the chat was
  // deleted and the backend returns 404). Without this, the stale chatId
  // would persist in localStorage and render forever as a loading skeleton
  // with no way to dismiss it.
  useEffect(() => {
    activeChats.forEach((chat_2, index) => {
      const query = chatDetails[index];
      if (chat_2.chatId && query?.isError) {
        removeActiveChat(chat_2.chatId);
      }
    });
  }, [activeChats, chatDetails, removeActiveChat]);

  // Hide errored chats from the display lists synchronously so they don't
  // briefly occupy a visible slot between render and the auto-evict effect.
  const validChats = useMemo(() => {
    return activeChats.filter((_chat, index_0) => !chatDetails[index_0]?.isError);
  }, [activeChats, chatDetails]);

  // The add-chat dialog occupies one visible slot when open.
  const visibleCount = isAddChatOpen ? GLOBAL_CHAT_CONSTANTS.MAX_VISIBLE_CHATS - 1 : GLOBAL_CHAT_CONSTANTS.MAX_VISIBLE_CHATS;
  const visibleChats = useMemo(() => validChats.slice(0, visibleCount), [validChats, visibleCount]);
  const hiddenChats = useMemo(() => validChats.slice(visibleCount), [validChats, visibleCount]);
  const openChat = async (patientId?: string, chatId_0?: string, onOpen?: (chatId_0: string) => void) => {
    if (patientId && chatId_0) {
      throw new Error("patientId and chatId cannot be provided together");
    }

    // On mobile, navigate to chat page instead of opening global chat window
    if (isMobile) {
      let finalChatId: string | undefined = chatId_0;
      closeSidebar(patientSidebarDefinition.id);

      // If patientId is provided, fetch the chat ID
      if (patientId) {
        try {
          const chatDetail = await queryClient.fetchQuery(chatQueryOptions.fromPatient(patientId));
          if (chatDetail?.id) {
            finalChatId = chatDetail.id;
          } else {
            throw new Error("Failed to get chat");
          }
        } catch (error) {
          const errorMessage = await parseError(error);
          toast({
            title: "Error",
            description: errorMessage,
            variant: "error"
          });
          return;
        }
      }
      if (finalChatId) {
        navigateWithProgress(`/comms/chat?chatId=${finalChatId}`);
        onOpen?.(finalChatId);
      } else {
        throw new Error("chatId must be provided");
      }
      return;
    }

    // Desktop behavior: open global chat window
    if (patientId) {
      return openChatByPatientId(patientId, onOpen);
    } else if (!chatId_0) {
      throw new Error("chatId must be provided");
    }
    onOpen?.(chatId_0);
    bringChatToFront(chatId_0);
  };
  const openChatByPatientId = async (patientId_0: string, onOpen_0?: (chatId: string) => void) => {
    const existingChatQueries = queryClient.getQueriesData<ChatDetail>({
      queryKey: chatQueryKeys.all
    });
    const matchingChatQuery = existingChatQueries?.find(([queryKey, data]) => {
      const detailKey = new Set(chatQueryKeys.detail().filter(key => key !== undefined));
      const fromPatientKey = new Set(chatQueryKeys.fromPatient(patientId_0));
      const queryKeySet = new Set(queryKey);
      if (queryKeySet.isSupersetOf(detailKey)) {
        const chat_3 = data as ChatDetail;
        if (!chat_3) return false;
        return chat_3.patients.some(p => p.id === patientId_0);
      }
      if (queryKeySet.isSupersetOf(fromPatientKey)) {
        if (!data) return false;
        return true;
      }
      return false;
    });
    const matchingChat = matchingChatQuery?.[1] as ChatDetail | undefined;
    if (matchingChat) {
      bringChatToFront(matchingChat.id);
      onOpen_0?.(matchingChat.id);
      return;
    }

    // The cache lookup above only matches once a request has resolved, so a
    // second call made while one is in flight would fall through and add its
    // own placeholder — one window per click, all resolving to the same chat.
    // React Query dedupes the request itself but not the placeholder we add
    // around it, so join the in-flight request instead of starting a second
    // open. Joining rather than bailing out matters: the request may have been
    // started by something other than a chat open (the opt-out queries on the
    // patient settings tab use the same key), and callers rely on `onOpen`
    // firing to finish their own work.
    if (queryClient.isFetching({
      queryKey: chatQueryKeys.fromPatient(patientId_0)
    })) {
      const joinedChat = await queryClient.fetchQuery(chatQueryOptions.fromPatient(patientId_0)).catch(() => null);
      if (joinedChat) {
        bringChatToFront(joinedChat.id);
        onOpen_0?.(joinedChat.id);
        return;
      }

      // The joined request failed, or this patient has no chat. Fall through to
      // the normal path so the failure surfaces — whoever owns that request may
      // be an opt-out query that never shows an error of its own.
    }

    // Create temporary chat window state immediately
    const tempChat: ChatWindowState = {
      chatId: "",
      isMinimized: false,
      localId: crypto.randomUUID()
    };

    // Add new chat to front of array (will appear as rightmost visible chat)
    addActiveChat(tempChat);
    try {
      // Get chat from API
      const chatDetail_0 = await queryClient.fetchQuery(chatQueryOptions.fromPatient(patientId_0));
      if (!chatDetail_0) {
        throw new Error("Failed to get chat");
      }

      // Update the temporary chat with real data while preserving minimized state
      updateActiveChat(tempChat, chat_4 => {
        const {
          localId: _localId,
          ...rest
        } = chat_4;
        return {
          ...rest,
          chatId: chatDetail_0.id,
          isMinimized: chat_4.isMinimized
        } satisfies ChatWindowState;
      });
      if (onOpen_0) {
        onOpen_0(chatDetail_0.id);
      }
    } catch (error_0) {
      removeActiveChat(tempChat);
      const errorMessage_0 = await parseError(error_0);
      toast({
        title: "Error",
        description: errorMessage_0,
        variant: "error"
      });
    }
  };
  const closeChat = (identifier_0: string | ChatWindowState) => {
    const chatsToKeep = activeChats.filter(chat_5 => {
      // Match by chatId / window-state ref first so a close still works
      // when the detail query has no cached data (deleted chat, errored
      // query). The cache lookup below is only needed for patientId-based
      // closes.
      if (typeof identifier_0 === "string") {
        if (chat_5.chatId === identifier_0) return false;
      } else if (chat_5 === identifier_0) {
        return false;
      }
      const chatData = queryClient.getQueryData<ChatDetail>(chatQueryOptions.detail(chat_5.chatId ?? "").queryKey);
      if (!chatData) return true;
      if (chatData.primaryPatientId === identifier_0) return false;
      if (chatData.patients.some(patient_0 => patient_0.id === identifier_0)) return false;
      return true;
    });
    setActiveChats(chatsToKeep);
  };
  const minimizeChat = (identifier_1: string | ChatWindowState) => {
    updateActiveChat(identifier_1, chat_6 => ({
      ...chat_6,
      isMinimized: true
    }));
  };

  // Clear activeCall when tenant changes
  // Note: The Zustand store with tenant storage handles activeChats automatically
  useEffect(() => {
    if (!tenant?.subDomain) return;
    if (previousTenantRef.current && previousTenantRef.current !== tenant.subDomain) {
      setActiveCall(null);
    }
    previousTenantRef.current = tenant.subDomain;
  }, [tenant?.subDomain]);
  return <CommunicationContext.Provider value={{
    activeCall,
    startCall,
    endCall,
    lastCallEnd,
    activeChats,
    openChat,
    closeChat,
    minimizeChat,
    maximizeChat,
    visibleChats,
    hiddenChats,
    isAddChatOpen,
    setIsAddChatOpen,
    chatFilters,
    selectedChatId,
    setChatFilters
  }}>
      {children}
    </CommunicationContext.Provider>;
}
export const useCommunication = () => {
  const context = useContext(CommunicationContext);
  if (context === undefined) {
    throw new Error("useCommunication must be used within a CommunicationProvider");
  }
  return context;
};